import React, { useState } from 'react';
import { Filter, ChevronDown, MessageSquare } from 'lucide-react';
import ReviewCard from './ReviewCard';

const ReviewList = ({ reviews, limit = 5, onSortChange, onFilterChange, currentSort = 'recent', currentFilters = {} }) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState(currentFilters);
  
  // Check if we have real reviews - NO placeholder/sample reviews
  const hasRealReviews = reviews && reviews.length > 0;
  
  if (!hasRealReviews) {
    // Return null - empty state is handled by ReviewStats
    return null;
  }

  const displayedReviews = limit ? reviews.slice(0, limit) : reviews;

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'helpful', label: 'Most Helpful' },
    { value: 'rating-high', label: 'Highest Rated' },
    { value: 'rating-low', label: 'Lowest Rated' }
  ];

  const skinTypeOptions = [
    { value: '', label: 'All Skin Types' },
    { value: 'dry', label: 'Dry' },
    { value: 'oily', label: 'Oily' },
    { value: 'combination', label: 'Combination' },
    { value: 'normal', label: 'Normal' },
    { value: 'sensitive', label: 'Sensitive' }
  ];

  const ageOptions = [
    { value: '', label: 'All Ages' },
    { value: 'under-18', label: 'Under 18' },
    { value: '18-24', label: '18-24' },
    { value: '25-34', label: '25-34' },
    { value: '35-44', label: '35-44' },
    { value: '45-54', label: '45-54' },
    { value: '55+', label: '55+' }
  ];

  const handleFilterChange = (filterType, value) => {
    const newFilters = { ...activeFilters, [filterType]: value };
    if (!value) delete newFilters[filterType];
    setActiveFilters(newFilters);
    if (onFilterChange) onFilterChange(newFilters);
  };

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  return (
    <div className="space-y-2">
      {/* Header with Sort & Filter - tighter spacing */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-[#ab3d22]" />
          <h3 className="text-sm font-semibold text-gray-800">
            Recent Reviews
            <span className="ml-1.5 text-xs font-normal text-gray-500">
              ({reviews.length})
            </span>
          </h3>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Sort dropdown */}
          <div className="relative">
            <select
              value={currentSort}
              onChange={(e) => onSortChange && onSortChange(e.target.value)}
              className="appearance-none bg-white border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#ab3d22]/20 focus:border-[#ab3d22] cursor-pointer shadow-sm"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3 w-3 text-gray-400 pointer-events-none" />
          </div>

          {/* Filter button */}
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-colors shadow-sm ${
              isFilterOpen || activeFilterCount > 0
                ? 'bg-[#ab3d22]/10 border-[#ab3d22]/30 text-[#ab3d22]'
                : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
          >
            <Filter className="h-3 w-3" />
            <span>Filter</span>
            {activeFilterCount > 0 && (
              <span className="bg-[#ab3d22] text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Filter Panel */}
      {isFilterOpen && (
        <div className="bg-white rounded-lg p-3 flex flex-wrap gap-3 border border-gray-200 shadow-sm">
          {/* Skin Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Skin:</span>
            <select
              value={activeFilters.skinType || ''}
              onChange={(e) => handleFilterChange('skinType', e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-200 rounded-md pl-2 pr-6 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#ab3d22]/20"
            >
              {skinTypeOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Age Filter */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Age:</span>
            <select
              value={activeFilters.ageRange || ''}
              onChange={(e) => handleFilterChange('ageRange', e.target.value)}
              className="appearance-none bg-gray-50 border border-gray-200 rounded-md pl-2 pr-6 py-1 text-xs text-gray-600 focus:outline-none focus:ring-1 focus:ring-[#ab3d22]/20"
            >
              {ageOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Clear filters */}
          {activeFilterCount > 0 && (
            <button
              onClick={() => {
                setActiveFilters({});
                if (onFilterChange) onFilterChange({});
              }}
              className="text-xs text-[#ab3d22] hover:underline ml-auto"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Reviews Grid - Tighter spacing */}
      <div className="space-y-1.5">
        {displayedReviews.map((review) => (
          <ReviewCard key={review._id} review={review} />
        ))}
      </div>

      {/* Load more indicator */}
      {reviews.length > limit && (
        <div className="text-center pt-2">
          <button className="text-xs text-[#ab3d22] hover:underline font-medium">
            View all {reviews.length} reviews →
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewList;
